import { formatDate, formatDateTimeLocale } from '@/common/utils/date';
import { CheckCircle, Edit, Eye, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { TRAMITE_LABELS } from '@/common/constants';
import { TramiteStatusDisplay, TramiteStatusDialog, useTramiteStatusDialog } from '@/components/TramiteStatus';
import { TareasProgressCell, TramiteTareasDialog, useTramiteTareasDialog } from '@/tramite-tareas';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Tramite } from '../types';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { getVencimientoPresentacion } from '@/hooks/useVencimientoPresentacion';
import { useHasAnyIndicatorPermission } from '@/hooks/useHasAnyIndicatorPermission';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';

interface TramiteFichaProps {
  tramite: Tramite;
  handleClickFinalizar: (tramiteId: number) => () => void;
}

const Campo: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={cn('min-w-0', className)}>
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 break-words font-medium text-foreground">{children}</dd>
  </div>
);

const TramiteFicha: React.FC<TramiteFichaProps> = ({ tramite, handleClickFinalizar }) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions ?? [];
  const canManageTramites = checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_TRAMITES.name]);
  const canSeeAnyIndicator = useHasAnyIndicatorPermission();
  const { isColumnVisible } = useColumnVisibility();
  const { isOpen, selectedTramite, openDialog, closeDialog } = useTramiteStatusDialog();
  const {
    isOpen: isTareasOpen,
    selectedTramite: selectedTareasTramite,
    openDialog: openTareasDialog,
    closeDialog: closeTareasDialog,
  } = useTramiteTareasDialog();
  const navigate = useNavigate();

  const [isSwipedLeft, setIsSwipedLeft] = useState(false);
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-80, 0], [1, 0]);

  if (!tramite) return null;

  const vendedor = tramite.vendedores?.[0]?.nombre || TRAMITE_LABELS.SIN_TRANSFERENTE;
  const comprador = tramite.compradores?.[0]?.nombre || TRAMITE_LABELS.SIN_ADQUIRIENTE;
  const { vencimientoPresentacion, diasEscritura } = getVencimientoPresentacion(tramite);
  const vencimientoProximo = canManageTramites
    && isColumnVisible('vencimientoPresentacion')
    && Boolean(tramite.fecescritura)
    && diasEscritura <= 10
    && diasEscritura > 0;

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.x < -50) {
      setIsSwipedLeft(true);
    } else if (info.offset.x > 50) {
      setIsSwipedLeft(false);
    }
  };

  const handleViewTramite = () => {
    navigate('/dashboard', { state: { tramite } });
  };

  const showHeader = isColumnVisible('id') || isColumnVisible('numregistro');

  return (
    <>
      <TramiteStatusDialog
        tramite={selectedTramite}
        isOpen={isOpen}
        onClose={closeDialog}
      />
      <TramiteTareasDialog
        tramite={selectedTareasTramite}
        isOpen={isTareasOpen}
        onClose={closeTareasDialog}
      />
      <article
        aria-label={`Escritura ${tramite.id}`}
        className="relative overflow-hidden rounded-lg border border-border bg-primary shadow-sm"
      >
        {/* Background quick action button */}
        <motion.div
          className="absolute right-0 top-0 bottom-0 flex items-center justify-center px-6"
          style={{ opacity }}
        >
          <button
            onClick={handleViewTramite}
            className="text-white flex flex-col items-center gap-1"
          >
            <ChevronRight className="h-6 w-6" />
            <span className="text-xs font-semibold">Ver</span>
          </button>
        </motion.div>

        {/* Swipeable card */}
        <motion.div
          className={cn(
            'relative bg-white text-sm',
            vencimientoProximo && 'border-l-4 border-l-orange-400 bg-orange-50'
          )}
          style={{ x }}
          drag="x"
          dragConstraints={{ left: -80, right: 0 }}
          dragElastic={0.2}
          onDragEnd={handleDragEnd}
          animate={{ x: isSwipedLeft ? -80 : 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {showHeader && (
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-2.5">
              {isColumnVisible('id') && (
                <span className="text-base font-semibold text-foreground">#{tramite.id}</span>
              )}
              {isColumnVisible('numregistro') && (
                <span className="rounded-full bg-background px-2.5 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-border">
                  Registro: {tramite.numregistro || '-'}
                </span>
              )}
            </header>
          )}

          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-3 min-[420px]:grid-cols-2">
            {isColumnVisible('vendedor') && (
              <Campo label={TRAMITE_LABELS.TRANSFERENTE}>{vendedor}</Campo>
            )}
            {isColumnVisible('comprador') && (
              <Campo label={TRAMITE_LABELS.ADQUIRIENTE}>{comprador}</Campo>
            )}
            {isColumnVisible('fecini') && (
              <Campo label="Fecha de inicio">{tramite.fecini ? formatDate(tramite.fecini) : '-'}</Campo>
            )}
            {isColumnVisible('fecfirma') && (
              <Campo label="Fecha Est. Firma">{tramite.fecfirma ? formatDateTimeLocale(tramite.fecfirma) : '-'}</Campo>
            )}
            {isColumnVisible('fecescritura') && (
              <Campo label="Fecha de escritura">{tramite.fecescritura ? formatDate(tramite.fecescritura) : '-'}</Campo>
            )}
            {isColumnVisible('num_escritura') && (
              <Campo label="# Escritura">{tramite.num_escritura || '-'}</Campo>
            )}
            {isColumnVisible('vencimientoPresentacion') && (
              <Campo label="Vencimiento presentación">
                <span className={cn(vencimientoProximo && 'text-orange-700')}>{vencimientoPresentacion}</span>
              </Campo>
            )}
            {canSeeAnyIndicator && isColumnVisible('estado') && (
              <div className="min-w-0">
                <TramiteStatusDisplay
                  tramite={tramite}
                  clickable={true}
                  onClick={openDialog}
                />
              </div>
            )}
            {isColumnVisible('tareas') && (
              <div className="min-w-0 min-[420px]:col-span-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tareas</dt>
                <dd className="mt-1">
                  <TareasProgressCell
                    tramite={tramite}
                    clickable
                    onClick={openTareasDialog}
                  />
                </dd>
              </div>
            )}
          </dl>

          {isColumnVisible('acciones') && (
            <footer className="flex justify-end border-t border-border px-4 py-2">
              <TooltipProvider>
                <div className="flex gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        asChild
                      >
                        <ReactRouterLink
                          to="/dashboard"
                          state={{ tramite }}
                          aria-label="Ver trámite"
                        >
                          <Eye className="h-4 w-4" />
                        </ReactRouterLink>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Ver trámite
                    </TooltipContent>
                  </Tooltip>
                  {canManageTramites && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          asChild
                        >
                          <ReactRouterLink
                            to="/tramite"
                            state={{ tramite }}
                            aria-label="Editar trámite"
                          >
                            <Edit className="h-4 w-4" />
                          </ReactRouterLink>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Editar trámite
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {canManageTramites && tramite.estado !== 'finalizado' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          aria-label="Finalizar trámite"
                          onClick={handleClickFinalizar(tramite.id)}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Finalizar trámite
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>
            </footer>
          )}
        </motion.div>
      </article>
    </>
  );
};

export default TramiteFicha;
